import type {
  GetAllProjectsNormalizedApiArg,
  GetFederationProjectsNormalizedApiArg,
  GetProjectNormalizedApiArg,
} from '../services/rest'
import type {ProjectId, RequestOptionsParams} from '../types'
import {ROOT_PROJECT_ID, stringifyId} from '../types'

import {buildTypeFields, linksAvailabilityField, linksField, parametersField} from './buildTypes'

const basicProjectFields = ['id', 'internalId', 'name', 'parentProjectId', 'archived', 'readOnlyUI']
const descriptionField = 'description'
const archivedProjectsIdField = 'projects(project(id),count,$locator(archived:true))'
const projectFields: (
  arg0: RequestOptionsParams | null | undefined,
) => ReadonlyArray<string> = options =>
  basicProjectFields
    .concat(
      options?.withBuildTypes === true
        ? `buildTypes(buildType(${buildTypeFields(options).join(',')}))`
        : [],
    )
    .concat(options?.withTemplates === true ? 'templates(buildType(id,name))' : [])
    .concat(options?.withVirtual === true ? 'virtual' : [])
    .concat(
      options?.withLinks === true
        ? linksField
        : options?.withLinksAvailability === true
          ? linksAvailabilityField
          : [],
    )
    .concat(options?.withParameters === true ? parametersField : [])
    .concat(options?.withDescription === true ? descriptionField : [])
    .concat(options?.withArchivedSubprojectsIds === true ? archivedProjectsIdField : [])
    .concat(
      options?.withPipelines === true ? 'pipelines(count,pipeline(id,name,parentProject))' : [],
    )
    .concat(
      options?.withAncestorProjects === true
        ? `ancestorProjects(project(${projectFields({withLinksAvailability: options?.withLinksAvailability}).join(',')}))`
        : [],
    )
export const getProjectsArg = (
  projectId: ProjectId,
  options?: RequestOptionsParams,
): GetAllProjectsNormalizedApiArg => {
  const childrenLocator = `archived:${options?.archived ? options.archived : 'false'},${
    options?.withLowNesting === true ? 'project' : 'affectedProject'
  }:${stringifyId(projectId)}`
  return {
    ...options,
    locator:
      options?.includeRoot === true
        ? `item(id:${stringifyId(projectId)}),item(${childrenLocator})`
        : childrenLocator,
    fields: `project(${projectFields({...options, withPipelines: true}).join(',')})`,
    projectId,
  }
}

export const archivedProjectsOptions: RequestOptionsParams = {
  withBuildTypes: true,
  withDescription: true,
  withLinksAvailability: true,
  archived: 'any',
}

export const sidebarOptions = {
  withBuildTypes: true,
  withAncestorProjects: true,
  withLinksAvailability: true,
  cache: true,
  projectReceiveMeta: {
    sidebarProjectsLoaded: true,
  },
}
export const sidebarArg = getProjectsArg(ROOT_PROJECT_ID, sidebarOptions)

export const overviewArg: GetAllProjectsNormalizedApiArg = {
  isOverview: true,
  cache: true,
  locator: 'archived:false,selectedByUser:(user:(current),mode:selected)',
  withAncestorProjects: true,
  fields:
    `project(${projectFields({archived: 'any', withPipelines: true, withAncestorProjects: true, withLinksAvailability: true}).join(',')},` +
    `buildTypes(buildType(${buildTypeFields({withLinksAvailability: true}).join(',')}),` +
    '$locator(selectedByUser:(user:current))))',
}

export const agentsProjectsArg = getProjectsArg(ROOT_PROJECT_ID, {
  withBuildTypes: false,
  withLinks: true,
})

export const getFederationProjectsArg = (
  federationServerUrl: string,
  withArchived: boolean,
): GetFederationProjectsNormalizedApiArg => ({
  ...getProjectsArg(ROOT_PROJECT_ID, {
    withBuildTypes: true,
    archived: withArchived ? 'any' : 'false',
  }),
  federationServerUrl,
})

export const getSingleProjectArg = (
  projectId: ProjectId,
  options: RequestOptionsParams = {
    withBuildTypes: true,
    withLinks: true,
    withParameters: true,
    withArchivedSubprojectsIds: true,
    withDescription: true,
    withPauseComment: true,
  },
): GetProjectNormalizedApiArg => ({
  ...options,
  projectLocator: `id:${stringifyId(projectId)}`,
  fields: projectFields({
    ...options,
    withPipelines: true,
    withVirtual: true,
  }).join(','),
  essential: true,
})
